import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  RefObject,
  MutableRefObject,
  createRef,
} from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '@store/hooks';
import { RootState } from '@store/store';
import {
  ChatBlock,
  ChatPlayData,
  FinalEnding,
  PreviousEnding,
} from '@/customTypes/chatStory';
import { isDevelopment } from '@common/utils';
import { PlayChapterInfo } from '@customTypes/chapter';
import { BadgesAcquired } from '@customTypes/story';
import { getBadgesToAchievement } from '@service/ChapterService';
import { setSeed } from '@lib/playerData';

/**
 * 2024-04-16
 * PlayerStatusProvider.tsx 로 신규 생성
 */

interface IChatPlayData {
  chatPlayData?: ChatPlayData;
  chatPlayDataRef: React.RefObject<ChatPlayData | undefined>;
  chatProgressRef: React.MutableRefObject<{
    index: number;
    sourceLine: number;
  }>;
  propsRef: RefObject<Record<string, number | string>>;
  itemsRef: RefObject<Record<string, number | string>>;
  blockNameHistoryRef?: MutableRefObject<ChatBlock[]>;

  updateProps: (userProps: Record<string, number | string>) => void;
  updateItems: (userItems: Record<string, number | string>) => void;
  updateAchievements: (achievementBadges: BadgesAcquired[]) => void;
  setChatProgress: ({
    index,
    sourceLine,
  }: {
    index: number;
    sourceLine: number;
  }) => void;
  setBlockHistory: ({
    chatBlocks,
    addBlock,
  }: {
    chatBlocks?: ChatBlock[];
    addBlock?: ChatBlock;
  }) => void;
  getPlayDataString: () => string;
}

const initChatStoryContext: IChatPlayData = {
  chatPlayData: undefined,
  chatPlayDataRef: createRef<undefined>(),
  chatProgressRef: createRef<any>(),
  propsRef: React.createRef<Record<string, number | string>>(),
  itemsRef: React.createRef<Record<string, number | string>>(),
  blockNameHistoryRef: undefined,
  updateProps: function (userProps: Record<string, number | string>): void {
    throw new Error('updateProps not implemented.');
  },
  updateItems: function (userItems: Record<string, number | string>): void {
    throw new Error('updateItems not implemented.');
  },
  updateAchievements: function (achievementBadges: BadgesAcquired[]): void {
    throw new Error('updateAchievements not implemented.');
  },
  setChatProgress: function ({
    index,
    sourceLine,
  }: {
    index: number;
    sourceLine: number;
  }): void {
    throw new Error('setChatProgress not implemented.');
  },
  setBlockHistory: ({
    chatBlocks,
    addBlock,
  }: {
    chatBlocks?: ChatBlock[];
    addBlock?: ChatBlock;
  }) => {
    throw new Error('setBlockHistory not implemented.');
  },
  getPlayDataString: () => '',
};

const ChatStoryContext = createContext<IChatPlayData>(initChatStoryContext);

const ChatPlayDataProvider = ({ children }: { children: ReactNode }) => {
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );

  const [chatPlayData, setChatPlayData] = React.useState<
    ChatPlayData | undefined
  >(undefined);

  const chatPlayDataRef = useRef<ChatPlayData | undefined>(undefined);

  const propsRef = useRef<Record<string, number | string>>({});
  const itemsRef = useRef<Record<string, number | string>>({});
  const achievementsRef = useRef<Record<string, number | string>>({});
  const prevEndingRef = useRef<PreviousEnding | undefined>(undefined);
  const finalEndingsRef = useRef<FinalEnding[] | undefined>(undefined);

  // id : name 형태의 object로 관리

  const chatProgressRef = useRef<{
    index: number;
    sourceLine: number;
  }>({
    index: -1,
    sourceLine: -1,
  });
  const blockNameHistoryRef = useRef<ChatBlock[]>([]);

  useEffect(() => {
    playChapterInfo && initChatPlayData(playChapterInfo);
  }, [playChapterInfo]);

  const initChatPlayData = (playChapterInfo: PlayChapterInfo) => {
    if (playChapterInfo) {
      propsRef.current = playChapterInfo.userProps;
      itemsRef.current = playChapterInfo.userItems;
      achievementsRef.current = playChapterInfo.achievements || {};

      const lastEnding = playChapterInfo.playedEndings.find(
        (ending) => ending.isPrevChapterEnding,
      );
      if (lastEnding) {
        prevEndingRef.current = {
          id: lastEnding?.endingId,
          name: lastEnding?.name,
        };
      }

      const finalEndings = playChapterInfo.playedEndings
        .filter((ending) => ending.isFinal && !!ending.updatedAt)
        .map((ending) => {
          return {
            id: ending.endingId,
            name: ending.name,
          };
        });
      if (finalEndings?.length > 0) {
        finalEndingsRef.current = finalEndings;
      }

      chatPlayDataRef.current = {
        tag: {
          name: 'PlayData',
          version: 220920,
        },
        props: propsRef.current,
        items: itemsRef.current,
        achievements: achievementsRef.current,
        previousEnding: lastEnding && {
          id: lastEnding?.endingId,
          name: lastEnding?.name,
        },
        finalEndings: finalEndings?.length > 0 ? finalEndings : undefined,
      };
      setChatPlayData(chatPlayDataRef.current);
      setSeed(String(playChapterInfo.upcId));
    }
  };

  const updateProps = (userProps: Record<string, number | string>) => {
    if (propsRef.current) {
      propsRef.current = userProps;
      setPlayData();
    }
  };

  const updateItems = (userItems: Record<string, number | string>) => {
    if (itemsRef.current) {
      itemsRef.current = userItems;
      setPlayData();
    }
  };

  const updateAchievements = (achievementBadges: BadgesAcquired[]) => {
    const acquiredAchievements = getBadgesToAchievement(achievementBadges);
    if (achievementsRef.current && acquiredAchievements) {
      achievementsRef.current = {
        ...achievementsRef.current,
        ...acquiredAchievements,
      };
      setPlayData();
    }
  };

  const setChatProgress = ({
    index,
    sourceLine,
  }: {
    index: number;
    sourceLine: number;
  }) => {
    chatProgressRef.current = {
      index,
      sourceLine,
    };
  };

  const setPlayData = () => {
    if (chatPlayDataRef.current) {
      chatPlayDataRef.current = {
        ...chatPlayDataRef.current,
        props: propsRef.current,
        items: itemsRef.current,
        achievements: achievementsRef.current,
      };
    }

    if (isDevelopment()) {
      setChatPlayData((prev) => {
        return (
          prev && {
            ...prev,
            props: propsRef.current || prev.props,
            items: itemsRef.current || prev.items,
            achievements: achievementsRef.current || prev.achievements,
          }
        );
      });
    }
  };

  const setBlockHistory = ({
    chatBlocks,
    addBlock,
  }: {
    chatBlocks?: ChatBlock[];
    addBlock?: ChatBlock;
  }) => {
    if (isDevelopment()) {
      addBlock && blockNameHistoryRef.current.push(addBlock);
      chatBlocks && (blockNameHistoryRef.current = chatBlocks);
    }
  };

  const getPlayDataString = () => {
    return JSON.stringify(chatPlayDataRef.current);
  };

  return (
    <ChatStoryContext.Provider
      value={{
        chatPlayData,
        chatPlayDataRef,
        chatProgressRef,
        propsRef,
        itemsRef,
        blockNameHistoryRef,
        updateProps,
        updateItems,
        updateAchievements,
        setChatProgress,
        setBlockHistory,
        getPlayDataString,
      }}
    >
      {children}
    </ChatStoryContext.Provider>
  );
};

export const useChatPlayDataContext = () => {
  const state = useContext(ChatStoryContext);
  if (!state) throw new Error('ChatPlayDataContext is undefined!');
  return state;
};

export default ChatPlayDataProvider;
